// Live mode of a device page (docs/device-protocol.md §16): the page asks the API for it when it opens, renews it
// every 2 minutes while it is visible and someone uses it, and stops after 10 minutes without activity (the
// device then returns to normal by itself). Each probe's values arrive on the organisation's live socket; the page
// keeps the last 10 minutes of them for a small curve, starting from the readings already stored.

import type { LiveProbeValue, LiveSession, SeriesResponse } from '@cck/schema';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api.ts';
import { onLiveProbe } from './live.ts';

/** Renewal period of a live session (the API keeps it 5 minutes). */
export const LIVE_RENEW_MS = 120_000;
/** Without activity on the page for this long, live mode is no longer renewed. */
export const LIVE_IDLE_MS = 10 * 60_000;
/** The curve's span. */
export const LIVE_WINDOW_MS = 10 * 60_000;

export interface ProbeLive {
  /** [time ms, °C], oldest first, within LIVE_WINDOW_MS. */
  points: [number, number][];
  last: { at: number; value: number; live: boolean } | null;
  /** When the value last changed (for the pulse); 0 before any live value. */
  changedAt: number;
}

export type LiveValues = Record<string, ProbeLive>;

/** Adds one value: kept in time order, without duplicates, within the window ending at `now`. */
export function addValue(values: LiveValues, probeId: string, at: number, value: number, live: boolean, now: number,
  fromHistory = false): LiveValues {
  const prev = values[probeId] ?? { points: [], last: null, changedAt: 0 };
  const points = prev.points.filter(([t]) => t !== at && t >= now - LIVE_WINDOW_MS);
  if (at >= now - LIVE_WINDOW_MS) {
    let i = points.length;
    while (i > 0 && points[i - 1]![0] > at) i--;
    points.splice(i, 0, [at, value]);
  }
  const newer = !prev.last || at >= prev.last.at;
  const last = newer ? { at, value, live } : prev.last;
  const changedAt = newer && !fromHistory && (!prev.last || prev.last.value !== value) ? now : prev.changedAt;
  return { ...values, [probeId]: { points, last, changedAt } };
}

export type LiveStatus = 'starting' | 'waiting' | 'live' | 'paused' | 'unavailable';

export interface DeviceLive {
  status: LiveStatus;
  session: LiveSession | null;
  /** How often values arrive: 10 s once live samples come, else what the API expects. */
  intervalS: number | null;
  /** A value came for a probe the page does not list yet (a probe discovered meanwhile). */
  unknownProbe: number;
  values: LiveValues;
  /** Ticks every second, for "3 s ago" and the countdowns. */
  now: number;
  resume: () => void;
}

/**
 * Starts and renews live mode for a device while the page is open. `probeIds` are its temperature probes: their
 * last 10 minutes of stored readings start the curves.
 */
export function useDeviceLive(orgId: string, deviceId: string, probeIds: string[], enabled: boolean,
  liveIntervalS = 10): DeviceLive {
  const [session, setSession] = useState<LiveSession | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [paused, setPaused] = useState(false);
  const [values, setValues] = useState<LiveValues>({});
  const [received, setReceived] = useState(false);
  const [liveSamples, setLiveSamples] = useState(false);
  const [unknownProbe, setUnknownProbe] = useState(0);
  const known = useRef(new Set<string>());
  known.current = new Set(probeIds);
  const [now, setNow] = useState(() => Date.now());
  const activity = useRef(Date.now());
  const ids = probeIds.join(',');

  // Values from the socket, for this device only.
  useEffect(() => {
    if (!enabled) return;
    return onLiveProbe(orgId, (v: LiveProbeValue) => {
      if (v.device_id !== deviceId) return;
      setReceived(true);
      if (v.live) setLiveSamples(true);
      if (!known.current.has(v.probe_id)) setUnknownProbe((n) => n + 1);
      setValues((prev) => addValue(prev, v.probe_id, Date.parse(v.ts), v.value_c, v.live, Date.now()));
    });
  }, [orgId, deviceId, enabled]);

  // The curves start from the readings already stored.
  useEffect(() => {
    if (!enabled || !ids) return;
    const to = new Date(Date.now() + 60_000).toISOString();
    const from = new Date(Date.now() - LIVE_WINDOW_MS).toISOString();
    let cancelled = false;
    api<SeriesResponse>(`/orgs/${orgId}/series?probe_ids=${ids}&from=${encodeURIComponent(from)}`
      + `&to=${encodeURIComponent(to)}&tier=raw`).then((r) => {
      if (cancelled) return;
      setValues((prev) => {
        let next = prev;
        const t = Date.now();
        for (const s of r.series) {
          for (const p of s.points) next = addValue(next, s.probe_id, Date.parse(p[0] as string), p[1] as number, false, t, true);
        }
        return next;
      });
    }).catch(() => undefined);  // the curve then starts empty
    return () => {
      cancelled = true;
    };
  }, [orgId, ids, enabled]);

  // Start, then renew while the page is visible and used.
  useEffect(() => {
    if (!enabled || paused || unavailable) return;
    let stopped = false;
    const start = async () => {
      try {
        const s = await api<LiveSession>(`/orgs/${orgId}/devices/${deviceId}/live`, { method: 'POST' });
        if (!stopped) setSession(s);
      } catch (err) {
        const status = (err as { status?: number }).status;
        // 409: not an active gateway; 403: a read-only support session. Rate limits and outages retry at the next tick.
        if (!stopped && (status === 409 || status === 403 || status === 404)) setUnavailable(true);
      }
    };
    void start();
    const timer = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - activity.current > LIVE_IDLE_MS) {
        setPaused(true);
        return;
      }
      void start();
    }, LIVE_RENEW_MS);
    const active = () => {
      activity.current = Date.now();
    };
    const visible = () => {
      if (document.visibilityState === 'visible') {
        active();
        void start();  // back to the tab: live mode may have lapsed meanwhile
      }
    };
    const events = ['pointermove', 'pointerdown', 'keydown', 'scroll', 'wheel'] as const;
    for (const e of events) window.addEventListener(e, active, { passive: true });
    document.addEventListener('visibilitychange', visible);
    return () => {
      stopped = true;
      clearInterval(timer);
      for (const e of events) window.removeEventListener(e, active);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [orgId, deviceId, enabled, paused, unavailable]);

  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [enabled]);

  const resume = useCallback(() => {
    activity.current = Date.now();
    setPaused(false);
  }, []);

  const expired = session !== null && Date.parse(session.live_until) <= now;
  const status: LiveStatus = unavailable ? 'unavailable' : paused || expired ? 'paused' : !session ? 'starting'
    : session.state === 'live' || received ? 'live' : 'waiting';
  const intervalS = liveSamples ? liveIntervalS : session?.interval_s ?? null;
  return { status, session, intervalS, unknownProbe, values, now, resume };
}
